<script lang="ts" setup>
  import { onMounted, useId, useTemplateRef } from 'vue';

  const props = withDefaults(defineProps<{ title: string; dismissible?: boolean }>(), { dismissible: true });
  defineEmits<{ close: [] }>();

  const titleId = useId();
  const dialog = useTemplateRef<HTMLDialogElement>('dialog');

  const dismiss = () => {
    if (props.dismissible) dialog.value?.close();
  };

  onMounted(() => dialog.value?.showModal());

  defineExpose({ close: () => dialog.value?.close() });
</script>

<template>
  <dialog
    ref="dialog"
    :aria-labelledby="titleId"
    @cancel.prevent="dismiss"
    @click.self="dismiss"
    @close="$emit('close')"
  >
    <div class="sheet">
      <h2 :id="titleId" class="title">{{ title }}</h2>
      <div class="body">
        <slot />
      </div>
      <div class="actions">
        <slot name="actions" />
      </div>
    </div>
  </dialog>
</template>

<style scoped>
  dialog {
    background: var(--color-backdrop);
    border: none;
    color: inherit;
    height: 100dvh;
    margin: 0;
    max-height: none;
    max-width: none;
    padding: 1rem var(--view-gutter) var(--modal-bottom-inset);
    width: 100%;

    &[open] {
      align-items: flex-end;
      display: flex;
      justify-content: center;
    }

    &::backdrop {
      background: transparent;
    }
  }

  .sheet {
    background: var(--color-surface);
    border-radius: var(--radius-card);
    box-shadow: 0 10px 25px var(--color-shadow);
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    height: var(--modal-height);
    max-width: var(--modal-max-width);
    padding: 1.5rem 2rem;
    width: 100%;
  }

  .title {
    color: var(--color-primary);
    font-size: var(--font-size-lg);
    margin: 0;
    text-align: center;
  }

  .body {
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 1.5rem;
    justify-content: safe center;
    min-height: 0;
    overflow-y: auto;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &:empty {
      display: none;
    }
  }
</style>
